import { FC, useState } from "react";
import { createPortal } from "react-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Spinner } from "@/components/ui";

import { STATE_BY_ID } from "./regions";

/* ── Types ─────────────────────────────────────── */

export interface UsMapProps {
  /** Fill color per state FIPS id. */
  colors: Record<string, string>;
  onHover?: (stateId: string | null) => void;
  onSelect: (stateId: string) => void;
  selected: string | null;
  /** Tooltip line per state FIPS id. */
  tooltips: Record<string, string>;
}

interface GeoFeature {
  id: string;
  rsmKey: string;
}

/* ── Constants ─────────────────────────────────── */

const GEOGRAPHY_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const HEIGHT = 500;
const SELECTED_STROKE = "rgb(var(--color-typography-900))";
const STROKE = "#fff";
const WIDTH = 800;

/* ── Component ─────────────────────────────────── */

const UsMap: FC<UsMapProps> = ({
  colors,
  onHover,
  onSelect,
  selected,
  tooltips,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  return (
    <Box
      style={{
        aspectRatio: WIDTH / HEIGHT,
        position: "relative",
        width: "100%",
      }}
    >
      {!loaded && (
        <Box className="absolute inset-0 items-center justify-center">
          <Spinner color="rgb(var(--color-primary-500))" size="small" />
        </Box>
      )}
      <ComposableMap
        height={HEIGHT}
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        style={{ display: "block", height: "100%", width: "100%" }}
        width={WIDTH}
      >
        <Geographies
          geography={GEOGRAPHY_URL}
          // @ts-expect-error react-simple-maps Feature type mismatch
          parseGeographies={(data: GeoFeature[]) =>
            data.filter((g) => STATE_BY_ID.has(g.id))
          }
        >
          {({ geographies }: { geographies: GeoFeature[] }) => {
            if (!loaded) setTimeout(() => setLoaded(true), 0);
            return geographies.map((geo) => {
              const isSelected = selected === geo.id;
              const fill = colors[geo.id] ?? "rgb(var(--color-background-200))";
              const style = {
                cursor: "pointer",
                fill,
                outline: "none",
                stroke: isSelected ? SELECTED_STROKE : STROKE,
                strokeWidth: isSelected ? 1.5 : 0.75,
                transition: "fill 300ms ease",
              };
              return (
                <Geography
                  geography={geo}
                  key={geo.rsmKey}
                  onClick={() => onSelect(geo.id)}
                  onMouseEnter={(e) => {
                    onHover?.(geo.id);
                    setTooltip({
                      text: tooltips[geo.id] ?? "",
                      x: e.clientX,
                      y: e.clientY,
                    });
                  }}
                  onMouseLeave={() => {
                    onHover?.(null);
                    setTooltip(null);
                  }}
                  onMouseMove={(e) =>
                    setTooltip({
                      text: tooltips[geo.id] ?? "",
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                  style={{
                    default: style,
                    hover: {
                      ...style,
                      stroke: SELECTED_STROKE,
                      strokeWidth: 1.25,
                    },
                    pressed: style,
                  }}
                />
              );
            });
          }}
        </Geographies>
      </ComposableMap>

      {tooltip &&
        createPortal(
          <div
            style={{
              background: "rgb(var(--color-background-800))",
              borderRadius: 6,
              color: "rgb(var(--color-typography-0))",
              fontSize: 12,
              left: tooltip.x + 12,
              padding: "4px 8px",
              pointerEvents: "none",
              position: "fixed",
              top: tooltip.y + 12,
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {tooltip.text}
          </div>,
          document.body
        )}
    </Box>
  );
};

export default UsMap;
